/**
 * Facebook OAuth Callback Handler
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle, Facebook } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export default function FacebookCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting your Facebook account...');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const isPopup = window.opener && window.opener !== window;

    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'Authorization was denied or cancelled');
      if (isPopup) {
        window.opener.postMessage({
          type: 'FACEBOOK_OAUTH_ERROR',
          error: errorDescription || 'Authorization was denied or cancelled',
        }, window.location.origin);
        window.close();
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received');
      if (isPopup) {
        window.opener.postMessage({
          type: 'FACEBOOK_OAUTH_ERROR',
          error: 'No authorization code received',
        }, window.location.origin);
        window.close();
      }
      return;
    }

    try {
      // Scope the new page(s) to the business account selected when connecting
      // (localStorage is shared with the opener tab).
      const businessAccountId = localStorage.getItem('selectedBusinessAccountId') || null;
      const response = await apiClient.post('/facebook/oauth/exchange', { code, business_account_id: businessAccountId });

      if (response.success) {
        setStatus('success');
        const pages = response.data?.pages || [];
        const pageCount = pages.length;

        if (pageCount > 0) {
          const pageName = pages[0]?.name || 'your Facebook page';
          setMessage(`Successfully connected ${pageName}!`);
        } else {
          setMessage('Facebook account connected successfully!');
        }

        if (isPopup) {
          window.opener.postMessage({
            type: 'FACEBOOK_OAUTH_SUCCESS',
            data: response.data,
          }, window.location.origin);

          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          setTimeout(() => {
            navigate('/home/social-media/facebook');
          }, 2000);
        }
      } else {
        throw ApiError.fromResult(response, 'Failed to connect account');
      }
    } catch (error: any) {
      setStatus('error');

      let errorMsg = error.response?.data?.error || error.error || error.message || 'Failed to connect Facebook account';

      // Provide helpful message for no pages found
      if (errorMsg.includes('No Facebook pages found')) {
        errorMsg = 'No Facebook pages found. During the Facebook login, please grant access to your page when prompted. If the issue persists, go to Facebook Settings → Business Integrations, remove this app, and try connecting again.';
      }

      setMessage(errorMsg);

      if (isPopup) {
        window.opener.postMessage({
          type: 'FACEBOOK_OAUTH_ERROR',
          error: errorMsg,
        }, window.location.origin);
        setTimeout(() => {
          window.close();
        }, 1500);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="bg-blue-600 p-4 rounded-full text-white inline-block">
              <Facebook className="h-8 w-8" />
            </div>
          </div>
          <CardTitle>Facebook Connection</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
              <p className="text-lg font-semibold text-green-700">{message}</p>
              <p className="text-sm text-muted-foreground">
                {window.opener && window.opener !== window ? 'Closing popup...' : 'Redirecting...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <p className="text-lg font-semibold text-red-700">Connection Failed</p>
              <p className="text-sm text-muted-foreground">{message}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button onClick={() => navigate('/home/social-media/facebook')} variant="outline">
                  Go Back
                </Button>
                <Button onClick={handleCallback}>Try Again</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
